import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, createVNode as _createVNode, withCtx as _withCtx, unref as _unref, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "section content-container" }
const _hoisted_2 = {
  key: 1,
  class: "container is-fluid"
}
const _hoisted_3 = { class: "columns" }
const _hoisted_4 = { class: "column" }
const _hoisted_5 = { class: "island-body" }
const _hoisted_6 = { class: "columns" }
const _hoisted_7 = { class: "column is-12" }
const _hoisted_8 = { class: "control" }
const _hoisted_9 = { class: "island-body" }
const _hoisted_10 = { class: "columns" }
const _hoisted_11 = { class: "column is-12" }
const _hoisted_12 = { class: "island-body" }
const _hoisted_13 = { class: "columns" }
const _hoisted_14 = { class: "column is-12" }

import { workspacesApi } from '@/workspaces/workspaces';
import Toast from '@/shared/services/toast';
import ReadonlyCopyableField from '@/shared/components/ReadonlyCopyableField.vue';
import TitledSection from '@/shared/components/TitledSection.vue';
import { messagingWorkspacesApi } from '@/messaging/messaging';
import { AddOrReplaceMessagingWorkspaceRequest } from '@/messaging/models/addOrReplaceMessagingWorkspaceRequest';
import { HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import { UsageLimitType } from '@/features/settings/models/usageLimitType';
import { UpdateWorkspaceViewModel } from '@/workspaces/models/request/updateWorkspaceViewModel';
import { MAX_INTEGER } from '@/shared/constants';
import { PROACTIVE_FLOWS_ENABLED } from '@/store/app/featureTypes';
import { useStore } from 'vuex';
import { computed, onMounted, reactive, ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required, minValue, maxValue } from '@vuelidate/validators';
import VuelidateUtil from '@shared/util/vuelidateUtil';
import { useVuelidateTouchWatcher } from '@shared/util/useVuelidateTouchWatcher';


export default /*@__PURE__*/_defineComponent({
  __name: 'GeneralSettings',
  setup(__props) {

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const workspaceUsageLimit = computed(() => store.getters.workspaceUsageLimit);
const isWorkspaceFeatureEnabled = computed(() => store.getters.isWorkspaceFeatureEnabled);
const handleSeriousError = (error: string) => store.dispatch(HANDLE_SERIOUS_ERROR, error);

const workspaceName = ref('');
const messageRetentionInDays = ref(90);
const appUserRetentionInDays = ref(365);
const maxProactiveTriggers = ref(0);
const updateInProgress = ref(false);
const isLoading = ref(true);
const showUsageLimits = ref(false);

const validations = {
  workspaceName: { required },
  messageRetentionInDays: { required, minValue: minValue(1), maxValue: maxValue(365) },
  appUserRetentionInDays: { required, minValue: minValue(1), maxValue: maxValue(365) },
  maxProactiveTriggers: { required, minValue: minValue(0), maxValue: maxValue(MAX_INTEGER) },
};

const $v = useVuelidate(validations, {
  workspaceName,
  messageRetentionInDays,
  appUserRetentionInDays,
  maxProactiveTriggers,
});

onMounted(async () => {
  try {
    if (isWorkspaceFeatureEnabled.value(PROACTIVE_FLOWS_ENABLED)) {
      showUsageLimits.value = true;
    }
    workspaceName.value = currentWorkspace.value.name;
    const workspaceFromMessaging = await messagingWorkspacesApi.getWorkspace(currentWorkspace.value.id);
    messageRetentionInDays.value = workspaceFromMessaging.messageRetentionInDays;
    appUserRetentionInDays.value = workspaceFromMessaging.appUserRetentionInDays;
    maxProactiveTriggers.value = workspaceUsageLimit.value(UsageLimitType.ProactiveTriggers);
  } catch (error: any) {
    if (error.status !== 404) {
      handleSeriousError(error.message);
    } else {
      messageRetentionInDays.value = 90;
      appUserRetentionInDays.value = 365;
    }
  } finally {
    isLoading.value = false;

    useVuelidateTouchWatcher($v, [
      ['workspaceName', workspaceName],
      ['messageRetentionInDays', messageRetentionInDays],
      ['appUserRetentionInDays', appUserRetentionInDays],
      ['maxProactiveTriggers', maxProactiveTriggers],
    ]);
  }
});

async function updateWorkspace() {
  if (!updateInProgress.value) {
    try {
      updateInProgress.value = true;
      const request = createUpdateRequest();
      await workspacesApi.update(currentWorkspace.value.id, request);
      currentWorkspace.value.name = request.name;
      currentWorkspace.value.usageLimits = request.usageLimits;

      await messagingWorkspacesApi.addOrReplace(createAddOrReplaceMessagingWorkspaceRequest());

      Toast.showSuccess(`Successfully updated workspace ${currentWorkspace.value.name}`);
    } catch (err: any) {
      Toast.showError(err.message);
    } finally {
      updateInProgress.value = false;
    }
  }
}

function getErrorMessage(prop: string, label: string) {
  return VuelidateUtil.getErrorMessage($v, prop, label);
}

function createAddOrReplaceMessagingWorkspaceRequest(): AddOrReplaceMessagingWorkspaceRequest {
  return {
    id: currentWorkspace.value.id,
    name: currentWorkspace.value.name,
    messageRetentionInDays: messageRetentionInDays.value,
    appUserRetentionInDays: appUserRetentionInDays.value,
  };
}

function createUpdateRequest() {
  if (showUsageLimits.value) {
    return {
      name: workspaceName.value,
      usageLimits: {
        [UsageLimitType.ProactiveTriggers]: maxProactiveTriggers.value,
      },
    } as UpdateWorkspaceViewModel;
  } else {
    return {
      name: workspaceName.value,
    } as UpdateWorkspaceViewModel;
  }
}

return (_ctx: any,_cache: any) => {
  const _component_n_loading_spinner = _resolveComponent("n-loading-spinner")!
  const _component_n_text_input = _resolveComponent("n-text-input")!
  const _component_n_number_input = _resolveComponent("n-number-input")!
  const _component_n_button = _resolveComponent("n-button")!

  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    (isLoading.value)
      ? (_openBlock(), _createBlock(_component_n_loading_spinner, { key: 0 }))
      : _createCommentVNode("", true),
    (!isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(TitledSection, { class: "container is-fluid island" }, {
                title: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                  _createTextVNode("Workspace", -1)
                ]))]),
                subtitle: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                  _createTextVNode("Change your workspace name", -1)
                ]))]),
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("div", _hoisted_6, [
                      _createElementVNode("div", _hoisted_7, [
                        _cache[7] || (_cache[7] = _createElementVNode("label", { class: "label" }, "Workspace ID", -1)),
                        _createVNode(ReadonlyCopyableField, {
                          readonlyValue: currentWorkspace.value.id,
                          onCopyMessage: "Copied to clipboard"
                        }, null, 8, ["readonlyValue"]),
                        _cache[8] || (_cache[8] = _createElementVNode("label", {
                          class: "label",
                          for: "name"
                        }, "Workspace name", -1)),
                        _createElementVNode("div", _hoisted_8, [
                          _createVNode(_component_n_text_input, {
                            id: "name",
                            modelValue: workspaceName.value,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((workspaceName).value = $event)),
                            errorMessage: getErrorMessage('workspaceName', 'Name')
                          }, null, 8, ["modelValue", "errorMessage"])
                        ])
                      ])
                    ])
                  ])
                ]),
                _: 1
              }),
              _createVNode(TitledSection, { class: "container is-fluid island" }, {
                title: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                  _createTextVNode("Retention Policies", -1)
                ]))]),
                subtitle: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                  _createTextVNode("Define how long you want to retain your customer's data", -1)
                ]))]),
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_9, [
                    _createElementVNode("div", _hoisted_10, [
                      _createElementVNode("div", _hoisted_11, [
                        _createVNode(_component_n_number_input, {
                          id: "messageRetentionInDays",
                          modelValue: messageRetentionInDays.value,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((messageRetentionInDays).value = $event)),
                          label: "Messages (in days)",
                          min: 1,
                          max: 365,
                          errorMessage: getErrorMessage('messageRetentionInDays', 'Retention policy')
                        }, null, 8, ["modelValue", "errorMessage"]),
                        _cache[11] || (_cache[11] = _createElementVNode("br", null, null, -1)),
                        _createVNode(_component_n_number_input, {
                          id: "appUserRetentionInDays",
                          modelValue: appUserRetentionInDays.value,
                          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((appUserRetentionInDays).value = $event)),
                          label: "Inactive leads (in days)",
                          min: 1,
                          max: 365,
                          errorMessage: getErrorMessage('appUserRetentionInDays', 'Retention policy')
                        }, null, 8, ["modelValue", "errorMessage"])
                      ])
                    ])
                  ])
                ]),
                _: 1
              }),
              (showUsageLimits.value)
                ? (_openBlock(), _createBlock(TitledSection, {
                    key: 0,
                    class: "container is-fluid island"
                  }, {
                    title: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                      _createTextVNode("Usage Limits", -1)
                    ]))]),
                    subtitle: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                      _createTextVNode("Set usage limits for proactive triggers", -1)
                    ]))]),
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_12, [
                        _createElementVNode("div", _hoisted_13, [
                          _createElementVNode("div", _hoisted_14, [
                            _createVNode(_component_n_number_input, {
                              id: "max-proactive-triggers",
                              modelValue: maxProactiveTriggers.value,
                              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((maxProactiveTriggers).value = $event)),
                              label: "Proactive triggers (per month)",
                              min: 0,
                              errorMessage: getErrorMessage('maxProactiveTriggers', 'Usage limit')
                            }, null, 8, ["modelValue", "errorMessage"])
                          ])
                        ])
                      ])
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              _createVNode(_component_n_button, {
                onClick: _cache[4] || (_cache[4] = ($event: any) => (updateWorkspace())),
                variant: "primary",
                loading: updateInProgress.value,
                disabled: _unref($v).$invalid
              }, {
                default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                  _createTextVNode(" Save ", -1)
                ]))]),
                _: 1
              }, 8, ["loading", "disabled"])
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})